import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useAuth, apiClient } from "@/App";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  ShoppingBag,
  Dumbbell,
  Zap,
  Users,
  Wallet,
  Scan,
  ChevronRight,
  Star,
  Trophy,
  TrendingUp,
  Calendar,
  Package,
  Gift,
} from "lucide-react";

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentProducts, setRecentProducts] = useState([]);
  const [referralData, setReferralData] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, productsRes, referralRes] = await Promise.all([
        apiClient.get("/dashboard/stats"),
        apiClient.get("/products?category=wear"),
        apiClient.get("/referral/code").catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setRecentProducts(productsRes.data.slice(0, 4));
      setReferralData(referralRes.data);
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { name: "BE Wear", icon: ShoppingBag, path: "/wear", color: "bg-primary" },
    { name: "BE Fit", icon: Dumbbell, path: "/fit", color: "bg-secondary" },
    { name: "BE Train", icon: Zap, path: "/train", color: "bg-blue-500" },
    { name: "BE Life", icon: Users, path: "/life", color: "bg-purple-500" },
    { name: "BE Wallet", icon: Wallet, path: "/wallet", color: "bg-accent" },
    { name: "Refer", icon: Gift, path: "/referral", color: "bg-gradient-to-br from-primary to-secondary" },
  ];

  const tierProgress = {
    bronze: { next: "silver", points: 1000, color: "bg-orange-600" },
    silver: { next: "gold", points: 5000, color: "bg-gray-400" },
    gold: { next: "platinum", points: 10000, color: "bg-yellow-500" },
    platinum: { next: null, points: 10000, color: "bg-purple-500" },
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const currentTier = stats?.tier || "bronze";
  const tierInfo = tierProgress[currentTier];
  const progress = tierInfo.next
    ? Math.min((stats?.points_balance || 0) / tierInfo.points * 100, 100)
    : 100;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="dashboard">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading font-bold text-3xl md:text-4xl mb-2">
            Welcome back, {user?.name?.split(" ")[0] || "Champion"}!
          </h1>
          <p className="text-muted-foreground">
            Your journey continues. Here's what's happening.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {/* Points Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="wallet-card text-foreground overflow-hidden" data-testid="points-card">
              <CardContent className="p-6 relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <Wallet className="w-8 h-8" />
                  <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${tierInfo.color} text-white`}>
                    {currentTier}
                  </span>
                </div>
                <div className="font-heading font-bold text-4xl mb-1 points-counter">
                  {stats?.points_balance?.toLocaleString() || 0}
                </div>
                <div className="text-sm opacity-80">BE Points</div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Orders Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card
              data-testid="orders-card"
              onClick={() => navigate("/orders")}
              className="cursor-pointer hover:border-primary transition-colors"
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Package className="w-8 h-8 text-primary" />
                  <TrendingUp className="w-5 h-5 text-secondary" />
                </div>
                <div className="font-heading font-bold text-4xl mb-1">
                  {stats?.order_count || 0}
                </div>
                <div className="text-sm text-muted-foreground">Orders</div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Workout Plans */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card data-testid="plans-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Zap className="w-8 h-8 text-blue-500" />
                  <Trophy className="w-5 h-5 text-accent" />
                </div>
                <div className="font-heading font-bold text-4xl mb-1">
                  {stats?.workout_plans_count || 0}
                </div>
                <div className="text-sm text-muted-foreground">Workout Plans</div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Body Scan Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card data-testid="scan-status-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Scan className="w-8 h-8 text-pink-500" />
                  {stats?.has_body_scan ? (
                    <Star className="w-5 h-5 text-secondary fill-secondary" />
                  ) : null}
                </div>
                <div className="font-heading font-bold text-lg mb-1">
                  {stats?.has_body_scan ? "Profile Complete" : "Not Yet Scanned"}
                </div>
                <div className="text-sm text-muted-foreground">
                  {stats?.has_body_scan ? "Perfect fit ready" : "Complete your profile"}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Tier Progress */}
        {tierInfo.next && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-8"
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading font-semibold">Progress to {tierInfo.next}</h3>
                    <p className="text-sm text-muted-foreground">
                      {tierInfo.points - (stats?.points_balance || 0)} points to go
                    </p>
                  </div>
                  <Trophy className={`w-8 h-8 ${currentTier === "bronze" ? "text-orange-600" : currentTier === "silver" ? "text-gray-400" : "text-yellow-500"}`} />
                </div>
                <Progress value={progress} className="h-3" />
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-8"
        >
          <h2 className="font-heading font-semibold text-xl mb-4">Quick Actions</h2>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {quickActions.map((action, index) => (
              <button
                key={action.name}
                onClick={() => navigate(action.path)}
                className="group p-4 bg-card rounded-2xl border border-border hover:border-primary transition-all hover:-translate-y-1"
                data-testid={`quick-action-${action.name.toLowerCase().replace(" ", "-")}`}
              >
                <div className={`w-12 h-12 ${action.color} rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                  <action.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-sm font-medium text-center">{action.name}</div>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Featured Products */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">New Arrivals</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate("/wear")} data-testid="view-all-products">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {recentProducts.map((product) => (
                    <Link
                      key={product.product_id}
                      to={`/product/${product.product_id}`}
                      className="group"
                      data-testid={`product-${product.product_id}`}
                    >
                      <div className="aspect-square rounded-xl overflow-hidden mb-2">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <h4 className="font-medium text-sm truncate">{product.name}</h4>
                      <p className="text-primary font-bold">${product.price}</p>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Subscription & Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="space-y-6"
          >
            {/* Subscription Status */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">BE Fit Box</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.has_subscription ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-secondary">
                        {stats.subscription_type?.toUpperCase()} Plan Active
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Your wellness box is on the way!
                      </p>
                    </div>
                    <Button variant="outline" onClick={() => navigate("/fit")} data-testid="manage-sub-btn">
                      Manage
                    </Button>
                  </div>
                ) : (
                  <div>
                    <p className="text-muted-foreground mb-4">
                      Get monthly wellness products delivered to your door.
                    </p>
                    <Button onClick={() => navigate("/fit")} className="w-full" data-testid="subscribe-btn">
                      Subscribe Now
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Body Scan CTA */}
            {!stats?.has_body_scan && (
              <Card className="bg-gradient-to-br from-secondary/10 to-primary/10 border-secondary/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                      <Scan className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading font-semibold mb-1">Complete Your Profile</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Get personalized size recommendations and earn 100 points!
                      </p>
                      <Button onClick={() => navigate("/profile")} variant="secondary" data-testid="complete-scan-btn">
                        Start Body Scan
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Upcoming Events */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">Upcoming Events</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate("/life")} data-testid="view-events-btn">
                  See All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center text-white">
                    <Calendar className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-medium">BE Bootcamp</h4>
                    <p className="text-sm text-muted-foreground">Earn 200 points</p>
                  </div>
                  <Button size="sm" className="ml-auto" onClick={() => navigate("/life")} data-testid="rsvp-event-btn">
                    RSVP
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Referral Promo */}
            <Card className="bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10 border-primary/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                    <Gift className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold mb-1">Refer Friends, Earn 300 pts</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      {referralData?.referral_code ? (
                        <>Your code: <span className="font-mono font-bold text-primary">{referralData.referral_code}</span></>
                      ) : (
                        "Share your code and both you and your friend earn rewards!"
                      )}
                    </p>
                    <Button onClick={() => navigate("/referral")} size="sm" data-testid="referral-cta-btn">
                      Share & Earn
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
